<script setup lang="ts">
  import { computed } from 'vue'
  import { useTranslation } from '@/composables/useTranslation'
  import { AlertTriangle } from 'lucide-vue-next'

  const props = defineProps<{
    companyName: string
    legalForm: string
    onFix: () => void
  }>()

  const { t } = useTranslation({
    en: {
      legalFormWarning:
        'Tip: Enter company name without the legal form (e.g., "Jupus" not "Jupus GmbH").',
      legalFormFixButton: 'Fix it'
    },
    de: {
      legalFormWarning:
        'Tipp: Geben Sie den Firmennamen ohne Rechtsform ein (z.B. "Jupus" statt "Jupus GmbH").',
      legalFormFixButton: 'Korrigieren'
    }
  })

  const hasLegalFormInName = computed(() => {
    if (!props.companyName || !props.legalForm) {
      return false
    }
    const nameUpper = props.companyName.trim().toUpperCase()
    const formUpper = props.legalForm.trim().toUpperCase()
    return nameUpper.endsWith(formUpper)
  })

  const suggestedCompanyName = computed(() => {
    if (!hasLegalFormInName.value || !props.companyName || !props.legalForm) {
      return ''
    }
    const pattern = new RegExp(
      `[\\s,\\-]*${props.legalForm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}$`,
      'i'
    )
    return props.companyName.replace(pattern, '').trim()
  })

  const warningMessage = computed(() => {
    if (!props.legalForm) return t('legalFormWarning')
    return t('legalFormWarning').replace('GmbH', props.legalForm)
  })

  const handleFix = () => {
    props.onFix()
  }
</script>

<template>
  <div
    v-if="hasLegalFormInName"
    class="mt-3 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3"
  >
    <AlertTriangle class="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600" />
    <div class="flex-1">
      <p class="text-sm text-amber-800">
        {{ warningMessage }}
      </p>
      <button
        type="button"
        class="mt-2 text-sm font-medium text-amber-900 underline hover:text-amber-700"
        @click="handleFix"
      >
        {{ t('legalFormFixButton') }}: "{{ suggestedCompanyName }}"
      </button>
    </div>
  </div>
</template>
